import { useState, useEffect, useCallback } from 'react';
import { Album, AlbumPhoto, CATEGORY_COLORS, CATEGORY_ICON } from '@/mocks/portfolio';

interface AlbumDetailProps {
  album: Album;
  onClose: () => void;
}

export default function AlbumDetail({ album, onClose }: AlbumDetailProps) {
  const [lightboxPhoto, setLightboxPhoto] = useState<AlbumPhoto | null>(null);

  const currentIdx = lightboxPhoto
    ? album.photos.findIndex((p) => p.id === lightboxPhoto.id)
    : -1;

  const handlePrev = useCallback(() => {
    if (currentIdx <= 0) return;
    setLightboxPhoto(album.photos[currentIdx - 1]);
  }, [currentIdx, album.photos]);

  const handleNext = useCallback(() => {
    if (currentIdx >= album.photos.length - 1) return;
    setLightboxPhoto(album.photos[currentIdx + 1]);
  }, [currentIdx, album.photos]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (lightboxPhoto) setLightboxPhoto(null);
        else onClose();
      }
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxPhoto, onClose, handlePrev, handleNext]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div className="fixed inset-0 z-[90] bg-[#0a0908] flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-6 py-5 border-b border-white/8 bg-[#0d0d0d]">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center bg-white/8 hover:bg-white/15 rounded-full text-white/70 hover:text-white transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg" />
          </button>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${CATEGORY_COLORS[album.category]}`}>
                <i className={`${CATEGORY_ICON[album.category]} text-xs`} />
                {album.category}
              </span>
            </div>
            <h2 className="text-white font-bold text-lg leading-tight">{album.title}</h2>
            <p className="text-white/35 text-xs flex items-center gap-1 mt-0.5">
              <i className="ri-map-pin-line" />
              {album.location}
              <span className="mx-1 text-white/20">·</span>
              <i className="ri-image-line" />
              {album.photos.length} {album.photos.length === 1 ? 'photo' : 'photos'}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center bg-white/8 hover:bg-white/15 rounded-full text-white/70 hover:text-white transition-all cursor-pointer"
        >
          <i className="ri-close-line text-xl" />
        </button>
      </div>

      {/* Description */}
      {album.description && (
        <div className="flex-shrink-0 px-6 py-4 bg-[#111]/60 border-b border-white/5">
          <p className="text-white/45 text-sm leading-relaxed max-w-3xl">{album.description}</p>
        </div>
      )}

      {/* Photo Grid */}
      <div className="flex-1 overflow-y-auto px-6 py-8">
        {album.photos.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-20">
            <div className="w-20 h-20 flex items-center justify-center bg-white/5 rounded-2xl mb-5">
              <i className="ri-image-add-line text-white/20 text-4xl" />
            </div>
            <p className="text-white/30 text-base font-medium mb-2">No photos yet</p>
            <p className="text-white/20 text-sm">Photos for this album will appear here once added.</p>
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4 max-w-7xl mx-auto">
            {album.photos.map((photo, idx) => (
              <div
                key={photo.id}
                className="break-inside-avoid relative group rounded-xl overflow-hidden cursor-pointer"
                onClick={() => setLightboxPhoto(photo)}
              >
                {photo.type === 'video' ? (
                  <div className="relative w-full bg-black/40">
                    <video
                      src={photo.src}
                      className="w-full h-auto object-cover"
                      muted
                      playsInline
                      preload="metadata"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/20 transition-colors duration-300">
                      <div className="w-14 h-14 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full border border-white/30 group-hover:scale-110 transition-transform duration-300">
                        <i className="ri-play-fill text-white text-2xl ml-1" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <img
                      src={photo.src}
                      alt={photo.caption || `${album.title} photo ${idx + 1}`}
                      className="w-full h-auto object-cover transition-transform duration-400 group-hover:scale-105"
                    />
                    <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-black/50 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <i className="ri-zoom-in-line text-white text-sm" />
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-[100] bg-black/97 flex items-center justify-center p-4"
          onClick={() => setLightboxPhoto(null)}
        >
          {/* Close */}
          <button
            type="button"
            className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer z-10"
            onClick={() => setLightboxPhoto(null)}
          >
            <i className="ri-close-line text-xl" />
          </button>

          {/* Counter */}
          <div className="absolute top-5 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white/60 text-xs font-semibold px-4 py-2 rounded-full border border-white/10">
            {currentIdx + 1} / {album.photos.length}
          </div>

          {/* Prev */}
          {currentIdx > 0 && (
            <button
              type="button"
              className="absolute left-4 md:left-8 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer z-10"
              onClick={(e) => { e.stopPropagation(); handlePrev(); }}
            >
              <i className="ri-arrow-left-line text-xl" />
            </button>
          )}

          {/* Image or Video */}
          <div
            className="relative max-w-5xl w-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {lightboxPhoto.type === 'video' ? (
              <video
                src={lightboxPhoto.src}
                controls
                autoPlay
                className="max-h-[78vh] w-auto max-w-full rounded-xl"
                aria-label={lightboxPhoto.caption || album.title}
              />
            ) : (
              <img
                src={lightboxPhoto.src}
                alt={album.title}
                className="max-h-[78vh] w-auto max-w-full rounded-xl object-contain"
              />
            )}
            {lightboxPhoto.caption && (
              <p className="mt-3 text-white/40 text-sm text-center">{lightboxPhoto.caption}</p>
            )}
          </div>

          {/* Next */}
          {currentIdx < album.photos.length - 1 && (
            <button
              type="button"
              className="absolute right-4 md:right-8 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer z-10"
              onClick={(e) => { e.stopPropagation(); handleNext(); }}
            >
              <i className="ri-arrow-right-line text-xl" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
